// SystemOptimizationPanel.jsx — Admin-only system log of data quality issues.
// Pulls /api/products/advisor/data-quality and renders a clean breakdown of:
//   - issues (gaps, anomalies, snapshots without periods)
//   - coverage summary
//   - recent advisor runs
// Keeps these warnings out of the user-facing Products page (user request).
import React, { useEffect, useState, useCallback } from "react";
import { api } from "./lib/api";
import { formatKSADateTime } from "./lib/ksaTime";

export default function SystemOptimizationPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/products/advisor/data-quality");
      setData(data);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const rebuildDaily = async () => {
    if (!window.confirm("سيتم إعادة بناء التاريخ اليومي من جميع التقارير المرفوعة. متابعة؟"))
      return;
    setBusy(true);
    try {
      const { data } = await api.post("/products/snapshots/rebuild-daily-history");
      alert(data.ok
        ? `✅ تم البناء\n\nسجلات: ${data.daily_records_created}\nأيام مغطّاة: ${data.days_covered}\nقيم شاذة: ${data.anomalies_decrease}`
        : `⚠️ ${data.message || "تعذّر البناء"}`);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "خطأ");
    } finally { setBusy(false); }
  };

  if (loading) return <div className="card" style={{ padding: 24, textAlign: "center" }}>⏳ جارٍ تحميل سجل التحسين...</div>;
  if (!data) return <div className="card" style={{ padding: 24, color: "var(--muted)" }}>تعذّر تحميل البيانات.</div>;

  const s = data.summary || {};
  const issues = data.issues || [];
  const gaps = data.gaps || [];
  const anom = data.recent_anomalies || [];
  const runs = data.recent_advisor_runs || [];

  const sev = {
    critical: { color: "#DC2626", bg: "#FEE2E2", icon: "🚨" },
    warning:  { color: "#D97706", bg: "#FEF3C7", icon: "⚠️" },
    info:     { color: "#0891B2", bg: "#E0F2FE", icon: "ℹ️" },
  };

  return (
    <div data-testid="system-optimization-panel">
      {/* Header */}
      <div className="card" style={{ padding: 16, marginBottom: 14,
        background: "linear-gradient(135deg, #fff 0%, #FEF3C7 100%)",
        borderInlineStart: "4px solid #D97706" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", flexWrap: "wrap", gap: 10 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#92400E", marginBottom: 4 }}>
              📋 سجل تحسين النظام (Admin)
            </div>
            <div style={{ fontSize: 12, color: "var(--muted)", lineHeight: 1.7 }}>
              تنبيهات داخلية بنقص البيانات والفجوات الزمنية والقيم الشاذة. مخفية عن المستخدمين العاديين للحفاظ على نظافة التقارير.
              <br />
              آخر تحديث: {data.generated_at?.slice(0, 19).replace("T", " ")}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={load} data-testid="sys-refresh-btn"
              style={{ padding: "8px 14px", background: "#fff", color: "#92400E",
                border: "1px solid #D97706", borderRadius: 6, fontWeight: 700, fontSize: 12.5, cursor: "pointer" }}>
              🔄 تحديث
            </button>
            <button onClick={rebuildDaily} disabled={busy} data-testid="sys-rebuild-btn"
              style={{ padding: "8px 14px", background: "#D97706", color: "#fff",
                border: "none", borderRadius: 6, fontWeight: 700, fontSize: 12.5,
                cursor: busy ? "wait" : "pointer" }}>
              {busy ? "⏳ جارٍ البناء..." : "🔧 إعادة بناء التاريخ اليومي"}
            </button>
          </div>
        </div>
      </div>

      {/* Summary KPIs */}
      <div className="card" style={{ padding: 14, marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#92400E", marginBottom: 10 }}>📊 ملخص حالة البيانات</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
          <KPI label="تقارير مرفوعة" value={s.snapshots_total} />
          <KPI label="سجلات يومية" value={s.daily_records_total} />
          <KPI label="أيام مغطّاة" value={s.distinct_days} sub={s.earliest_day && s.latest_day ? `${s.earliest_day} → ${s.latest_day}` : ""} />
          <KPI label="نسبة التغطية" value={(s.coverage_pct || 0) + "%"} status={s.coverage_pct >= 90 ? "ok" : s.coverage_pct >= 60 ? "warn" : "err"} />
          <KPI label="قيم شاذة" value={s.anomalies_total} status={s.anomalies_total === 0 ? "ok" : "warn"} />
          <KPI label="منتجات جديدة" value={s.new_product_lines} />
          <KPI label="تقارير بدون فترة" value={s.snapshots_without_period} status={s.snapshots_without_period === 0 ? "ok" : "err"} />
          <KPI label="أيام بدون بيانات" value={s.uncovered_days || 0} status={s.uncovered_days > 0 ? "warn" : "ok"} />
        </div>
      </div>

      {/* Issues */}
      <div className="card" style={{ padding: 14, marginBottom: 14 }} data-testid="sys-issues-list">
        <div style={{ fontSize: 13, fontWeight: 700, color: "#92400E", marginBottom: 10 }}>
          🚨 المشاكل المكتشفة ({issues.length})
        </div>
        {issues.length === 0 ? (
          <div style={{ padding: 14, color: "#16A34A", fontSize: 13, textAlign: "center", background: "#F0FDF4", borderRadius: 6 }}>
            ✅ لا توجد مشاكل في البيانات حالياً
          </div>
        ) : (
          issues.map((it, i) => {
            const c = sev[it.severity] || sev.info;
            return (
              <div key={i} style={{ padding: 10, marginBottom: 6, background: c.bg,
                borderInlineStart: `3px solid ${c.color}`, borderRadius: 4 }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: c.color }}>
                  {c.icon} [{it.code}] {it.message}
                </div>
                {it.details?.length > 0 && (
                  <ul style={{ margin: "4px 16px 0", padding: 0, fontSize: 11.5, color: "#374151" }}>
                    {it.details.map((d, j) => (
                      <li key={j}>
                        {d.from} → {d.to} ({d.days} يوم)
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Gaps detail */}
      {gaps.length > 0 && (
        <div className="card" style={{ padding: 14, marginBottom: 14 }} data-testid="sys-gaps-list">
          <div style={{ fontSize: 13, fontWeight: 700, color: "#92400E", marginBottom: 10 }}>
            🕳 فجوات التواريخ ({gaps.length})
          </div>
          <div className="tw">
            <table>
              <thead><tr><th>#</th><th>من</th><th>إلى</th><th>عدد الأيام</th></tr></thead>
              <tbody>
                {gaps.map((g, i) => (
                  <tr key={i}><td>{i + 1}</td><td>{g.from}</td><td>{g.to}</td><td>{g.days}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Anomalies */}
      {anom.length > 0 && (
        <div className="card" style={{ padding: 14, marginBottom: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#92400E", marginBottom: 10 }}>
            ⚠️ قيم شاذة (آخر 20)
          </div>
          <div className="tw" style={{ maxHeight: 260, overflowY: "auto" }}>
            <table>
              <thead><tr><th>التاريخ</th><th>المنتج</th><th>الكمية</th><th>الإيراد</th></tr></thead>
              <tbody>
                {anom.map((r, i) => (
                  <tr key={i} style={{ background: "#FEE2E2" }}>
                    <td>{r.date}</td>
                    <td>{r.product_name}</td>
                    <td>{(r.qty || 0).toLocaleString("en-US")}</td>
                    <td>{(r.rev || 0).toLocaleString("en-US")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent advisor runs */}
      {runs.length > 0 && (
        <div className="card" style={{ padding: 14, marginBottom: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#92400E", marginBottom: 10 }}>
            🤖 آخر تحليلات الذكاء الاصطناعي
          </div>
          <ul style={{ margin: 0, padding: "0 16px", fontSize: 12.5, lineHeight: 1.8 }}>
            {runs.map((r, i) => (
              <li key={i}>
                <b>{r.product}</b> — {formatKSADateTime(new Date(r.analyzed_at), { dateStyle: "short", timeStyle: "short" })}
                {r.analyzed_by && <span style={{ color: "var(--muted)" }}> · بواسطة {r.analyzed_by}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function KPI({ label, value, sub, status }) {
  const colors = { ok: "#16A34A", warn: "#D97706", err: "#DC2626" };
  return (
    <div style={{ padding: 10, background: "#fff", borderRadius: 6, border: "1px solid var(--border)" }}>
      <div style={{ fontSize: 10.5, color: "var(--muted)" }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 800, color: status ? colors[status] : "#1F2937", marginTop: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
